import React, {FunctionComponent, useState} from "react";
import {Box, Typography, useTheme} from "@mui/material";
import {AChartX, AxisHoverParam} from "@atiautomacao/ati-ui-library";
import {AChartXProps} from "@atiautomacao/ati-ui-library/dist/components/AChartX/AChartX";
import {format, isValid} from "date-fns";

interface AnalysisChartWithHintProps {
    option: AChartXProps['option'];
    height: number;
}

const formatHintDate = (axisValue: string | number, axisValueLabel?: string): string => {
    const date = new Date(axisValue);
    if (typeof axisValue !== 'string' && isValid(date)) {
        return format(date, "dd/MM/yyyy HH:mm:ss");
    }
    return axisValueLabel ?? String(axisValue ?? '');
}

const getHintValue = (value: unknown): string | number => {
    const hintValue = Array.isArray(value) ? value[1] : value;
    return typeof hintValue === 'number' || typeof hintValue === 'string' ? hintValue : '-';
}

const AnalysisChartWithHint: FunctionComponent<AnalysisChartWithHintProps> = ({option, height}) => {
    const theme = useTheme();
    const [activeParams, setActiveParams] = useState<AxisHoverParam[]>([]);

    return (
        <Box
            onMouseLeave={() => setActiveParams([])}
            sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}
        >
            <AChartX
                option={option}
                height={height}
                loading={false}
                theme={theme.palette.mode === 'light' ? undefined : theme.palette.mode}
                onAxisHover={(params) => {
                    // O ocultamento fica no onMouseLeave do bloco, para o hint seguir visível com o mouse sobre ele
                    if (params) {
                        setActiveParams(params);
                    }
                }}
            />
            {/* Região live sempre montada para leitores de tela anunciarem as mudanças do hint */}
            <Box role="status" aria-live="polite">
                {activeParams.length > 0 && (
                    <Box sx={{
                        mt: 2,
                        mb: 1,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '12px',
                        p: 2,
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '8px',
                        position: 'relative',
                    }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ pr: 1, borderRight: `1px solid ${theme.palette.divider}`, mr: 1 }}>
                            {formatHintDate(activeParams[0].axisValue, activeParams[0].axisValueLabel)}
                        </Typography>

                        {activeParams.map((item) => (
                            <Box key={item.seriesIndex} sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color, mr: 0.5 }} />
                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                    {item.seriesName}:{' '}
                                    <Typography component="span" variant="body2" fontWeight="bold" color="text.primary">
                                        {getHintValue(item.value)}
                                    </Typography>
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default AnalysisChartWithHint;
